import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Chip from '@material-ui/core/Chip';

import utils from '../../universal/utils';

const POLL_INTERVAL_MS = 30000;

const styles = (theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(0.5),
  },
  chip: {
    height: 24,
    fontSize: '0.7rem',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: '1px solid #e0e0e0',
  },
  chipLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    paddingRight: 8,
  },
  heartbeat: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  heartbeatOk: {
    backgroundColor: '#2e7d32',
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  heartbeatError: {
    backgroundColor: '#c62828',
    opacity: 0.85,
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.35,
    },
  },
});

function getCatalogHealthUrl() {
  return '/is_alive';
}

function getEdgeHealthUrl() {
  return utils.getEdgeHealthUrl();
}

async function fetchHealth(url) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`Health check failed with status ${response.status}`);
  }

  return response.json();
}

class ServiceHealthBar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      catalog: null,
      edge: null,
    };
    this.pollTimer = null;
    this.refreshHealth = this.refreshHealth.bind(this);
  }

  componentDidMount() {
    this.refreshHealth();
    this.pollTimer = setInterval(this.refreshHealth, POLL_INTERVAL_MS);
  }

  componentWillUnmount() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  }

  async refreshHealth() {
    const edgeHealthUrl = getEdgeHealthUrl();
    const healthChecks = [fetchHealth(getCatalogHealthUrl())];

    if (edgeHealthUrl) {
      healthChecks.push(fetchHealth(edgeHealthUrl));
    }

    const [catalogResult, edgeResult] = await Promise.allSettled(healthChecks);

    this.setState({
      catalog:
        catalogResult.status === 'fulfilled'
          ? { ok: true, data: catalogResult.value }
          : { ok: false },
      edge: edgeResult
        ? edgeResult.status === 'fulfilled'
          ? { ok: true, data: edgeResult.value }
          : { ok: false }
        : null,
    });
  }

  renderServiceChip(label, healthState) {
    const { classes } = this.props;
    const isOk = healthState && healthState.ok && healthState.data?.status === 'ok';
    const version = healthState?.data?.version;
    const chipLabel = version ? `${label} v${version}` : label;

    return (
      <Chip
        key={label}
        className={classes.chip}
        label={
          <span className={classes.chipLabel}>
            <span
              className={`${classes.heartbeat} ${
                isOk ? classes.heartbeatOk : classes.heartbeatError
              }`}
              aria-hidden="true"
            />
            {chipLabel}
          </span>
        }
        size="small"
      />
    );
  }

  render() {
    const { classes } = this.props;
    const { catalog, edge } = this.state;

    if (!catalog && !edge) {
      return null;
    }

    return (
      <div className={classes.root} aria-label="Service health">
        {this.renderServiceChip('catalog', catalog)}
        {edge !== null && this.renderServiceChip('edge', edge)}
      </div>
    );
  }
}

ServiceHealthBar.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(ServiceHealthBar);
